import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, MapPin, Calendar, Clock, Check, Mail, Phone, User, Search, Navigation } from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { useWidgetMode } from "@/lib/widgetMode";

const TIME_SLOTS = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM",
  "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM",
  "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM",
  "5:00 PM"
];

const getNext14Days = () => {
  const days = [];
  const now = new Date();
  for (let i = 1; i <= 14; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() + i);
    // Skip Sundays
    if (d.getDay() === 0) continue;
    days.push({
      date: d.toISOString().split("T")[0],
      label: d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
      isToday: false
    });
  }
  return days;
};

const AppointmentModal = ({ open, onClose, offerId, lockedOfferId, existingContact }) => {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [step, setStep] = useState(1); // 1=location, 2=date/time, 3=contact/confirm
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [name, setName] = useState(existingContact?.name || "");
  const [email, setEmail] = useState(existingContact?.email || "");
  const [phone, setPhone] = useState(existingContact?.phone || "");
  const [zipSearch, setZipSearch] = useState("");
  const [searchedZip, setSearchedZip] = useState("");
  const days = getNext14Days();
  const { isCompact } = useWidgetMode();

  const fetchLocations = async (zip = "") => {
    try {
      const params = new URLSearchParams({ limit: "8" });
      if (zip) params.set("zip_code", zip);
      const res = await axios.get(`${API}/dealerships?${params}`);
      setLocations(res.data.dealerships);
      if (zip) setSearchedZip(zip);
    } catch {
      // Fallback to legacy
      axios.get(`${API}/locations`).then(res => setLocations(res.data.locations)).catch(() => {});
    }
  };

  useEffect(() => {
    if (open) fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/appointments/schedule`, {
        offer_id: offerId,
        locked_offer_id: lockedOfferId,
        location_id: selectedLocation.id,
        date: selectedDate,
        time_slot: selectedTime,
        contact: { name: name.trim(), email: email.trim(), phone: phone.trim() }
      });

      if (res.data.success) {
        setConfirmed(true);
        setConfirmData(res.data);
        toast.success("Appointment scheduled!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to schedule appointment");
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-3">
      {/* ZIP Search */}
      <div className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            inputMode="numeric"
            maxLength={5}
            placeholder="Enter ZIP to find nearest"
            value={zipSearch}
            onChange={(e) => setZipSearch(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => { if (e.key === "Enter" && zipSearch.length === 5) fetchLocations(zipSearch); }}
            className="pl-9 h-10 rounded-xl text-sm"
            data-testid="appt-zip-search"
          />
        </div>
        <Button
          onClick={() => zipSearch.length === 5 && fetchLocations(zipSearch)}
          disabled={zipSearch.length !== 5}
          size="sm"
          className="h-10 px-4 btn-primary disabled:opacity-50"
          data-testid="appt-zip-search-btn"
        >
          Search
        </Button>
      </div>
      {searchedZip && (
        <p className="text-xs text-slate-500">Showing locations nearest to <strong>{searchedZip}</strong></p>
      )}
      <div className="max-h-[340px] overflow-y-auto space-y-2 pr-1">
        {locations.map((loc, i) => (
          <button
            key={loc.id}
            onClick={() => { setSelectedLocation(loc); setStep(2); }}
            className={`w-full text-left p-4 rounded-xl border transition-all ${
              selectedLocation?.id === loc.id 
                ? "border-slate-800 bg-slate-50 shadow-sm" 
                : "border-slate-200 hover:border-slate-400 hover:shadow-sm"
            }`}
            data-testid={`location-${loc.id}`}
          >
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-slate-900">{loc.name}</p>
                  {searchedZip && i === 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold text-white" style={{ backgroundColor: "hsl(220, 70%, 15%)" }}>Closest</span>
                  )}
                </div>
                <p className="text-sm text-slate-500">{loc.address}{loc.city ? `, ${loc.city}, ${loc.state} ${loc.zip}` : ""}</p>
                {loc.distance_miles != null && (
                  <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                    <Navigation className="w-3 h-3" />
                    {loc.distance_miles} miles away
                  </p>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-5">
      <div className="p-3 bg-slate-100 rounded-xl flex items-center gap-2 text-sm">
        <MapPin className="w-4 h-4 text-slate-500" />
        <span className="font-medium">{selectedLocation?.name}</span>
        <button onClick={() => setStep(1)} className="ml-auto text-blue-600 text-xs hover:underline">Change</button>
      </div>

      {/* Date Selection */}
      <div>
        <p className="text-label mb-2">SELECT DATE</p>
        <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
          {days.map((day) => (
            <button
              key={day.date}
              onClick={() => setSelectedDate(day.date)}
              className={`p-2.5 rounded-lg text-sm text-center border transition-all ${
                selectedDate === day.date
                  ? "border-slate-800 bg-slate-900 text-white"
                  : "border-slate-200 hover:border-slate-400"
              }`}
              data-testid={`date-${day.date}`}
            >
              {day.label}
            </button>
          ))}
        </div>
      </div>

      {/* Time Selection */}
      {selectedDate && (
        <div>
          <p className="text-label mb-2">SELECT TIME</p>
          <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
            {TIME_SLOTS.map((slot) => (
              <button
                key={slot}
                onClick={() => setSelectedTime(slot)}
                className={`p-2.5 rounded-lg text-sm text-center border transition-all ${
                  selectedTime === slot
                    ? "border-slate-800 bg-slate-900 text-white"
                    : "border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`time-${slot.replace(/\s/g, '-')}`}
              >
                {slot}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedDate && selectedTime && (
        <Button onClick={() => setStep(3)} className="w-full h-12 btn-primary" data-testid="next-to-confirm-btn">
          Next
        </Button>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      {/* Summary */}
      <div className="p-4 bg-slate-50 rounded-xl space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-slate-500" />
          <span className="font-medium">{selectedLocation?.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-500" />
          <span>{new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-slate-500" />
          <span>{selectedTime}</span>
        </div>
      </div>

      {/* Contact Info */}
      <div>
        <p className="text-label mb-2">CONTACT INFORMATION</p>
        <div className="space-y-3">
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Your Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="pl-10 h-11 rounded-xl"
              data-testid="appt-name-input"
            />
          </div>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="email"
              placeholder="Email for confirmation"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 h-11 rounded-xl"
              data-testid="appt-email-input"
            />
          </div>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="tel"
              placeholder="Phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="pl-10 h-11 rounded-xl"
              data-testid="appt-phone-input"
            />
          </div>
        </div>
      </div>

      <Button
        onClick={handleConfirm}
        disabled={loading}
        className="w-full h-14 btn-accent text-base"
        data-testid="confirm-appointment-btn"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-slate-800/30 border-t-slate-800 rounded-full animate-spin" />
            Scheduling...
          </span>
        ) : (
          "Confirm Appointment"
        )}
      </Button>
    </div>
  );

  const renderConfirmed = () => (
    <div className="text-center py-6">
      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Check className="w-8 h-8 text-emerald-600" />
      </div>
      <h3 className="text-xl font-semibold text-slate-900 mb-2">Appointment Scheduled!</h3>
      <div className="text-sm text-slate-600 space-y-1 mb-6">
        <p className="font-medium">{selectedLocation?.name}</p>
        <p>{new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</p>
        <p>{selectedTime}</p>
        <p className="text-slate-500 mt-2">{selectedLocation?.address}</p>
        <p className="text-slate-500">{selectedLocation?.phone}</p>
      </div>
      <div className="p-3 bg-blue-50 rounded-xl text-sm text-slate-600 mb-6">
        Bring your vehicle, title, and a valid photo ID. Get paid the same day!
      </div>
      <Button onClick={onClose} className="w-full h-12 btn-primary" data-testid="close-appointment-modal">
        Done
      </Button>
    </div>
  );

  return (
    <div
      className={`fixed inset-0 z-50 flex ${
        isCompact ? "items-start" : "items-center"
      } justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto`}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col" data-testid="appointment-modal">
        {/* Header */}
        <div className="header-nucar px-6 py-5 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-white font-semibold text-lg">
              {confirmed ? "Confirmed!" : step === 1 ? "Choose Location" : step === 2 ? "Pick Date & Time" : "Confirm Details"}
            </h2>
            {!confirmed && (
              <p className="text-slate-300 text-sm mt-0.5">Step {step} of 3</p>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors" data-testid="close-appointment-modal-x">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {confirmed ? renderConfirmed() : step === 1 ? renderStep1() : step === 2 ? renderStep2() : renderStep3()}
        </div>

        {/* Back button */}
        {!confirmed && step > 1 && (
          <div className="px-6 pb-4 flex-shrink-0">
            <button 
              onClick={() => setStep(s => s - 1)} 
              className="text-sm text-slate-500 hover:text-slate-700"
              data-testid="appointment-back-btn"
            >
              ← Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentModal;
